import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, CardBody, Badge, Input, InputGroup } from "reactstrap";
import IntlMessages from "Util/IntlMessages";
import {
  LAYOUT_MAX_WIDTH_400,
  DASHBOARD_HR_ORG_INITIALS
} from "Constants/reactifyInlineStyles";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { suiteApi } from "../../../../../api/suiteAdapters";
import {
  AsyncStateBoundary,
  SuitePageShell
} from "../../_suite/primitives";

function tierColor(tier: string) {
  if (tier === "Enterprise") return "primary";
  if (tier === "Pro") return "info";
  return "secondary";
}

export default function CustomerDirectoryPage() {
  const [q, setQ] = useState("");
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ticketing.listCustomers(),
    []
  );

  const rows = useMemo(() => {
    const list = data || [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      c =>
        String(c.name).toLowerCase().includes(term) ||
        String(c.id).toLowerCase().includes(term)
    );
  }, [data, q]);

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.ticketing.customers" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Organization directory — link each card to your CRM / account record.
        </p>
      }
    >
      <InputGroup className="mb-4" style={LAYOUT_MAX_WIDTH_400}>
        <Input
          type="search"
          placeholder="Filter organizations…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </InputGroup>

      <AsyncStateBoundary
        loading={loading}
        error={error}
        empty={!loading && rows.length === 0}
      >
        <Row>
          {rows.map(org => (
            <Col md="6" xl="4" key={org.id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm hover-shadow">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div
                      className="rounded-circle bg-light d-flex align-items-center justify-content-center text-primary font-weight-bold"
                      style={DASHBOARD_HR_ORG_INITIALS}
                    >
                      {String(org.name).slice(0, 1)}
                    </div>
                    <Badge color={tierColor(org.tier)}>{org.tier}</Badge>
                  </div>
                  <h5 className="mb-1">{org.name}</h5>
                  <p className="text-muted small mb-2">{org.id}</p>
                  <Row className="text-center small">
                    <Col xs="4">
                      <div className="text-muted">Contacts</div>
                      <div className="h5 mb-0">{org.contacts}</div>
                    </Col>
                    <Col xs="4">
                      <div className="text-muted">Open tix</div>
                      <div className="h5 mb-0">{org.openTickets ?? "—"}</div>
                    </Col>
                    <Col xs="4">
                      <div className="text-muted">Region</div>
                      <div className="font-weight-medium">{org.region}</div>
                    </Col>
                  </Row>
                  {org.lastTouch && (
                    <p className="small text-muted mt-2 mb-0">
                      Last touch {org.lastTouch}
                    </p>
                  )}
                  <Link
                    className="btn btn-outline-primary btn-sm btn-block mt-3"
                    to={org.id}
                    relative="path"
                  >
                    <IntlMessages id="suite.action.open" />
                  </Link>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}
